import axios, { AxiosError, AxiosRequestConfig } from "axios";
import useSWR, { SWRConfiguration } from "swr";

import generalRequestErrorHandler from "./generalRequestErrorHandler";

interface IApiRequestPostConfig<TResponse, TRequest = any> {
	url: string | null;
	axiosConfig?: AxiosRequestConfig;
	swrConfig?: SWRConfiguration<{ data: TResponse; status: number }, AxiosError>;
	onError?: (error: AxiosError | Error) => void;
	body?: TRequest;
	triggerOnMount?: boolean;
}

interface IApiRequestPostResult<TResponse> {
	data: TResponse | undefined;
	status: number | undefined;
	isLoading: boolean;
	isValidating: boolean;
	error: AxiosError | undefined;
	getUpdateData: ReturnType<
		typeof useSWR<{ data: TResponse; status: number }, AxiosError>
	>["mutate"];
	postData: (
		overrideBody?: any,
		overrideConfig?: AxiosRequestConfig
	) => Promise<{ data: TResponse; status: number } | undefined>;
}

const poster = async <T>(
	url: string,
	body?: any,
	config?: AxiosRequestConfig
): Promise<{ data: T; status: number }> => {
	try {
		const response = await axios.post<T>(url, body, config);

		return { data: response.data, status: response.status };
	} catch (error: any) {
		generalRequestErrorHandler(error as AxiosError);
		throw error;
	}
};

const useApiRequestPost = <TResponse = any, TRequest = any>(
	config: IApiRequestPostConfig<TResponse, TRequest>
): IApiRequestPostResult<TResponse> => {
	const { triggerOnMount = false, ...restConfig } = config;

	const { data, error, mutate, isValidating, isLoading } = useSWR<
		{ data: TResponse; status: number },
		AxiosError
	>(
		restConfig.url,

		triggerOnMount
			? (url) => poster<TResponse>(url, restConfig.body, restConfig.axiosConfig)
			: null,
		{
			...restConfig.swrConfig,
			revalidateOnMount: triggerOnMount,
			onError: (err) => {
				if (restConfig.onError) {
					restConfig.onError(err);
				}
			},
		}
	);

	const customTrigger = async (
		overrideBody?: any,
		overrideConfig?: AxiosRequestConfig
	): Promise<{ data: TResponse; status: number } | undefined> => {
		if (!config.url) {
			return undefined;
		}

		try {
			const promise = poster<TResponse>(config.url, overrideBody ?? config.body, {
				...config.axiosConfig,
				...overrideConfig,
			});

			const result = await mutate(promise, {
				revalidate: false,
			});

			return result;
		} catch (err) {
			// Ensure the error state is set in SWR
			await mutate(undefined, {
				revalidate: false,
			});
			// Manually trigger error update
			throw err;
		}
	};

	return {
		data: data?.data,
		status: data?.status,
		isLoading,
		isValidating,
		error,
		getUpdateData: mutate,
		postData: customTrigger,
	};
};

export default useApiRequestPost;
